import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useEffect } from 'react';
import { toastFlashMessage } from 'utils';
import {
  getRoleplayProgressService,
  getRoleplaysToCompleteService,
  getRoleplaysToReviewService,
  getListUsersService,
  getListRoleplaysService,
  getListScenariosPerRoleplayService,
  createAssignRoleplay,
  createSelfAssignRoleplay,
  uploadRecordingService,
  updateRecordingInScenarioService,
  getRoleplayview,
  getRoleplayHistoryService,
  submitRoleplay,
  getRoleplayTeamService,
  reviewScenario,
  submitReview,
  roleplaysToPassService,
  createCustomRoleplay,
  unassignRoleplayService,
  deleteRoleplayService,
  reassignRoleplayService,
  getListUsersRepService,
  getListUserRepService,
  createReminder,
  getAssignmentRating,
} from './Roleplay.services';
import Organization from 'infrastructure/Organization';
import { useAutoFillStore } from './Components/Autorating/AutoFill.store';
import { useLocation, useHistory } from 'react-router-dom';

export const ROLEPLAY_CACHE_KEYS = {
  SCENARIOS: 'roleplay_scenarios',
  ASSIGNED_TO_ME: 'roleplay_to_complete',
  PROGRESS_GRAPH: 'roleplays_progress',
  TO_REVIEW: 'roleplay_to_review',
  LIST_USERS: 'roleplay_list_users',
  LIST_ROLEPLAYS: 'roleplay_list_roleplays',
  LIST_SCENARIOS_PER_ROLEPLAY: 'roleplay_list_scenarios_per_roleplay',
  ROLEPLAY_VIEW: 'roleplay_roleplay_view',
  HISTORY: 'roleplay_history',
  MY_TEAM: 'roleplay_my_team',
  TO_PASS: 'roleplay_to_pass_home',
  LIST_USERS_REPS: 'roleplay_list_users_reps',
  LIST_USER_ROLEPLAY: 'roleplay_list_user_roleplay',
};

// Roleplays Dashboard

export const useGetRoleplaysToCompleteQuery = ({ enabled }) => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.ASSIGNED_TO_ME],
    queryFn: getRoleplaysToCompleteService,
    enabled,
  });
};

export const useGetRoleplaysToReviewQuery = ({ enabled }) => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.TO_REVIEW],
    queryFn: () => getRoleplaysToReviewService(),
    enabled,
  });
};

const hasItemsToday = (data) => {
  if (!data || !Array.isArray(data)) return false;
  const today = new Date().toISOString().split('T')[0];
  return data.some((item) => {
    const itemDate = new Date(item?.updated_at).toISOString().split('T')[0];
    return itemDate === today;
  });
};

export const useGetRoleplayHistoryQuery = ({ enabled, time = 'all-time' }) => {
  const refetchCount = useRef(0);

  useEffect(() => {
    refetchCount.current = 0;
  }, [time]);

  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.HISTORY, time],
    queryFn: () => getRoleplayHistoryService({ time }),
    enabled,
    refetchInterval: (data) => {
      return hasItemsToday(data) && refetchCount.current < 3 ? 35000 : false;
    },
    refetchIntervalInBackground: true,
    onSuccess: () => {
      refetchCount.current += 1;
    },
  });
};

export const useGetRoleplayTeamQuery = ({ time = 'all-time', user = 'all' }) => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.MY_TEAM, time, user],
    queryFn: () => getRoleplayTeamService({ time, user }),
    enabled: !Organization.isRep(),
  });
};

export const useGetRoleplayProgressQuery = ({ home, time = 'all-time' }) => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.PROGRESS_GRAPH, home, time],
    queryFn: () => getRoleplayProgressService({ home, time }),
  });
};

export const useGetListUsersQuery = () => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.LIST_USERS],
    queryFn: getListUsersService,
  });
};

export const useGetListRoleplaysQuery = () => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.LIST_ROLEPLAYS],
    queryFn: getListRoleplaysService,
  });
};

export const useGetListScenariosPerRoleplaysQuery = ({ roleplayUuid = '' }) => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.LIST_SCENARIOS_PER_ROLEPLAY],
    queryFn: getListScenariosPerRoleplayService({ roleplayUuid }),
  });
};

export const useCreateAssignRoleplay = () => {
  const queryClient = useQueryClient();
  return useMutation(({ roleplay, version }) => createAssignRoleplay({ roleplay, version }), {
    onSuccess: (response) => {
      toastFlashMessage(response?.message, 'success');
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.ASSIGNED_TO_ME]);
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.PROGRESS_GRAPH]);
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.TO_REVIEW]);
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.MY_TEAM]);
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.LIST_USERS_REPS]);
    },
  });
};

export const useCreateSelfAssignRoleplay = () => {
  let location = useLocation();
  let history = useHistory();
  const queryClient = useQueryClient();
  return useMutation(({ roleplay, version }) => createSelfAssignRoleplay({ roleplay, version }), {
    onSuccess: (response) => {
      console.log(response?.uuid);
      toastFlashMessage(response?.message, 'success');
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.ASSIGNED_TO_ME]);
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.PROGRESS_GRAPH]);
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.TO_REVIEW]);
      history.push({
        pathname: `/roleplay/${response?.uuid}`,
        state: {
          prevPath: `${location.pathname}${location.search ? location.search : ''}`,
        },
      });
    },
  });
};

export const useGetRoleplayView = ({ roleplay_id, enabled }) => {
  return useQuery(
    [ROLEPLAY_CACHE_KEYS.ROLEPLAY_VIEW, roleplay_id],
    () => getRoleplayview({ roleplay_id }),
    {
      enabled,
    }
  );
};

// Recording

export const useUploadRecordingMutation = () => {
  return useMutation(uploadRecordingService);
};

export const useUpdateRecordingInScenarioMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ url_video, response_uuid, version }) =>
      updateRecordingInScenarioService({ url_video, response_uuid, version }),
    {
      onSuccess: (response, data) => {
        toastFlashMessage(response?.message, 'success');
        queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.ROLEPLAY_VIEW, data.roleplay_id]);
      },
    }
  );
};

//submit roleplay

export const useSubmitRoleplay = () => {
  const queryClient = useQueryClient();
  return useMutation(({ roleplay_id, version }) => submitRoleplay({ roleplay_id, version }), {
    onSuccess: (response) => {
      toastFlashMessage(response?.message, 'success');
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.MY_TEAM]);
    },
  });
};

export const useReviewScenario = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ roleplay, scenario, body, version }) =>
      reviewScenario({ roleplay, scenario, body, version }),
    {
      onSuccess: (response) => {
        toastFlashMessage(response?.message, 'success');
        queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.ROLEPLAY_VIEW]);
      },
    }
  );
};

export const useSubmitReview = () => {
  const queryClient = useQueryClient();
  return useMutation(({ roleplay_id, version }) => submitReview({ roleplay_id, version }), {
    onSuccess: (response) => {
      toastFlashMessage(response?.message, 'success');
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.MY_TEAM]);
    },
  });
};

const invalidateQueries = (response, queryClient) => {
  toastFlashMessage(response?.message, 'success');
  queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.TO_REVIEW]);
  queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.ASSIGNED_TO_ME]);
  queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.PROGRESS_GRAPH]);
  queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.MY_TEAM]);
};

export const useUnassignRoleplayMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(({ roleplay_id }) => unassignRoleplayService({ roleplay_id }), {
    onSuccess: (response) => invalidateQueries(response, queryClient),
  });
};
export const useDeleteRoleplayMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(({ roleplay_id }) => deleteRoleplayService({ roleplay_id }), {
    onSuccess: (response) => invalidateQueries(response, queryClient),
  });
};
export const useReassignRoleplayMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(({ roleplay_id }) => reassignRoleplayService({ roleplay_id }), {
    onSuccess: (response) => invalidateQueries(response, queryClient),
  });
};

// Home
export const useGetRoleplayToPassQuery = () => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.TO_PASS],
    queryFn: roleplaysToPassService,
  });
};

export const useCreateCustomRoleplay = () => {
  const queryClient = useQueryClient();
  return useMutation(({ roleplay }) => createCustomRoleplay({ roleplay }), {
    onSuccess: (response) => {
      toastFlashMessage(response?.message, 'success');
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.ASSIGNED_TO_ME]);
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.PROGRESS_GRAPH]);
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.TO_REVIEW]);
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.MY_TEAM]);
    },
  });
};

export const useGetListUsersRepQuery = () => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.LIST_USERS_REPS],
    queryFn: getListUsersRepService,
  });
};

export const useGetListUsersRepsV2Query = () => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.LIST_USERS_REPS, 'version_2'],
    queryFn: getListUsersRepService,
    enabled: !Organization.isRep(),
  });
};

export const useGetListUserRepQuery = ({ uuid = '' }) => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.LIST_USER_ROLEPLAY],
    queryFn: () => getListUserRepService({ uuid }),
  });
};

export const useGetListUserRepQueryV2 = ({ uuid = '' }) => {
  return useQuery({
    queryKey: [ROLEPLAY_CACHE_KEYS.LIST_USER_ROLEPLAY],
    queryFn: () => getListUserRepService({ uuid }),
    enabled: Organization.isRep(),
  });
};

export const useCreateReminder = () => {
  const queryClient = useQueryClient();
  return useMutation(({ roleplay_user_uuid }) => createReminder({ roleplay_user_uuid }), {
    onSuccess: (response) => {
      toastFlashMessage(response?.message, 'success');
      queryClient.invalidateQueries([ROLEPLAY_CACHE_KEYS.TO_REVIEW]);
    },
  });
};
//AUTORATING
export const useGetAssignmentRatingQuery = ({
  org_id,
  assignment_id,
  responseScenarios,
  enabled,
}) => {
  const setFilledWithAI = useAutoFillStore((state) => state?.setFilledWithAI);
  const setResponseData = useAutoFillStore((state) => state?.setResponseData);
  const toggleIsError = useAutoFillStore((state) => state?.toggleIsError);
  const reset = useAutoFillStore((state) => state?.reset);
  return useQuery({
    queryKey: ['AI', assignment_id],
    queryFn: () => getAssignmentRating({ org_id, assignment_id }),
    enabled,
    onSuccess: (response) => {
      const scenarios = response.scenarios;
      setFilledWithAI();
      if (Object.keys(scenarios).length === 0) {
        const newInfo = [
          {
            scenario_id: '',
            response_id: '',
            name: '',
            status: 'WITHOUT_SCENARIOS',
            started_at: '',
            finished_at: '',
            feedback: null,
            rating_criteria: null,
          },
        ];
        setResponseData(newInfo);
      } else {
        const updatedResponse = [];
        scenarios.forEach((item) => {
          const index = responseScenarios.findIndex(
            (element) => element?.scenario_id === item?.scenario_id
          );
          const reponse_id = responseScenarios[index]?.response_id;
          item?.responses?.forEach((x) => {
            if (x?.reponse_id === reponse_id) {
              updatedResponse.push({
                scenario_id: item?.scenario_id,
                response_id: x?.reponse_id,
                status: x.status,
                feedback: x?.feedback,
                rating_criteria: x?.rating_criteria,
                started_at: x?.started_at,
                finished_at: x?.finished_at,
              });
            }
          });
        });
        setResponseData(updatedResponse);
      }
    },
    onError: () => {
      toggleIsError();
      reset();
    },
  });
};
